import type { 
  PlanogramSubtask, 
  SubtaskStatus, 
  StatusChange, 
  SubtaskComment, 
  SubtaskAttachment 
} from '../types/plan-sub-task.ts';

// Generate unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

// Generate subtask code
const generateSubtaskCode = (index: number) => `ST${String(index).padStart(6, '0')}`;

// Mock status change history
const createStatusHistory = (subtaskCode: string, currentStatus: SubtaskStatus): StatusChange[] => {
  const history: StatusChange[] = [];
  const baseTime = new Date('2024-01-15T09:00:00Z');
  
  // Always start with Pending
  history.push({
    id: generateId(),
    fromStatus: null,
    toStatus: 'Pending',
    changeTime: new Date(baseTime.getTime()).toISOString(),
    changedBy: 'system',
    reason: 'System auto creation',
    notes: 'Subtask automatically generated based on milestone plan'
  });

  if (currentStatus === 'Pending') return history;

  // Add Active status
  history.push({
    id: generateId(),
    fromStatus: 'Pending',
    toStatus: 'Active',
    changeTime: new Date(baseTime.getTime() + 24 * 60 * 60 * 1000).toISOString(),
    changedBy: 'system',
    reason: 'Milestone start time reached',
    notes: 'System automatically updated task status to ready to start'
  });

  if (currentStatus === 'Active') return history;

  // Add Ongoing status
  history.push({
    id: generateId(),
    fromStatus: 'Active',
    toStatus: 'Ongoing',
    changeTime: new Date(baseTime.getTime() + 25 * 60 * 60 * 1000).toISOString(),
    changedBy: 'user001',
    reason: 'User started task execution',
    notes: 'Assignee confirmed to start task execution'
  });

  if (currentStatus === 'Ongoing') return history;

  // Add Paused status (can be from Active or Ongoing)
  if (currentStatus === 'Paused') {
    const fromStatus = history[history.length - 1].toStatus;
    history.push({
      id: generateId(),
      fromStatus: fromStatus as SubtaskStatus,
      toStatus: 'Paused',
      changeTime: new Date(baseTime.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      changedBy: 'system',
      reason: 'Plan suspended',
      notes: 'Task automatically paused due to related plan suspension'
    });
    return history;
  }

  // Add Finished status
  if (currentStatus === 'Finished') {
    history.push({
      id: generateId(),
      fromStatus: 'Ongoing',
      toStatus: 'Finished',
      changeTime: new Date(baseTime.getTime() + 72 * 60 * 60 * 1000).toISOString(),
      changedBy: 'user001',
      reason: 'Task execution completed',
      notes: 'Assignee confirmed task completion and submitted execution notes'
    });
  }

  // Add Cancelled status (can be cancelled from any status except Finished)
  if (currentStatus === 'Cancelled') {
    const fromStatus = 'Active'; // Default to cancelled from Active
    history.push({
      id: generateId(),
      fromStatus: fromStatus,
      toStatus: 'Cancelled',
      changeTime: new Date(baseTime.getTime() + 48 * 60 * 60 * 1000).toISOString(),
      changedBy: 'system',
      reason: 'Planogram plan adjustment - task no longer required',
      notes: 'Task cancelled due to changes in planogram requirements'
    });
  }

  return history;
};

// Mock subtasks data - Following PRD field definitions
export const MOCK_SUBTASKS: PlanogramSubtask[] = [
  {
    subtaskCode: generateSubtaskCode(1),
    subtaskName: 'Display Group Configuration Update',
    milestoneName: 'Display Group Adjustment',
    assignee: 'user001',
    status: 'Ongoing',
    gmtCreate: '2024-01-15T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-16T09:00:00Z',
    startDate: '2024-01-16T10:30:00Z',
    dueDate: '2024-01-20T18:00:00Z',
    executionNotes: 'Currently updating display group configuration, adjusting Sub-Class associations to optimize product grouping logic. Initial configuration has been completed and is under review.',
    deliverables: 'Updated Display Group configuration, Association mapping',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(1), 'Ongoing'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP001_52_20250127',
    planName: 'East China Major Adjustment Plan_W52'
  },
  {
    subtaskCode: generateSubtaskCode(2),
    subtaskName: 'Target Store List Determination',
    milestoneName: 'Store Group Setup',
    assignee: 'user002',
    status: 'Ongoing',
    gmtCreate: '2024-01-15T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-16T09:00:00Z',
    startDate: '2024-01-16T10:30:00Z',
    dueDate: '2024-01-18T18:00:00Z',
    deliverables: 'Determined target store list, Store scope analysis',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(2), 'Ongoing'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP002_05_20250210',
    planName: 'Southwest Regular Maintenance Plan_W05'
  },
  {
    subtaskCode: generateSubtaskCode(3),
    subtaskName: 'Benchmark Drawing Creation',
    milestoneName: 'Benchmark Master Drawing',
    assignee: 'user003',
    status: 'Finished',
    gmtCreate: '2024-01-10T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-11T09:00:00Z',
    startDate: '2024-01-11T09:15:00Z',
    dueDate: '2024-01-15T18:00:00Z',
    completionDate: '2024-01-14T16:30:00Z',
    executionNotes: 'Completed benchmark drawing creation using Nielsen software. All display diagrams have been reviewed and approved, ready for implementation.',
    deliverables: 'Benchmark display diagram, Technical specifications',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(3), 'Finished'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    finalStatusDate: '2024-01-14T16:30:00Z',
    planCode: 'TEMP003_06_20250215',
    planName: 'North China Minor Adjustment Plan_W06'
  },
  {
    subtaskCode: generateSubtaskCode(4),
    subtaskName: 'Physical Layout Implementation',
    milestoneName: 'Store Layout Adjustment',
    assignee: 'user004',
    status: 'Pending',
    gmtCreate: '2024-01-15T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-22T09:00:00Z',
    dueDate: '2024-01-25T18:00:00Z',
    deliverables: 'Updated store layout, Implementation report',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(4), 'Pending'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP004_04_20250201',
    planName: 'North China Emergency Adjustment Plan_W04'
  },
  {
    subtaskCode: generateSubtaskCode(5),
    subtaskName: 'New Fixture Type Configuration',
    milestoneName: 'Fixture Type Set Up',
    assignee: 'user005',
    status: 'Active',
    gmtCreate: '2024-01-15T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-16T09:00:00Z',
    dueDate: '2024-01-19T18:00:00Z',
    deliverables: 'New fixture type configuration, Master data setup',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(5), 'Active'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP005_09_20250301',
    planName: 'New Product Launch Special Plan_W09'
  },
  {
    subtaskCode: generateSubtaskCode(6),
    subtaskName: 'Section Numbering Update',
    milestoneName: 'Display Planning Adjustment',
    assignee: 'user001',
    status: 'Ongoing',
    gmtCreate: '2024-01-12T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-13T09:00:00Z',
    startDate: '2024-01-13T14:20:00Z',
    dueDate: '2024-01-17T18:00:00Z',
    deliverables: 'Updated display planning, Section mapping',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(6), 'Ongoing'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP001_52_20250127',
    planName: 'East China Major Adjustment Plan_W52'
  },
  {
    subtaskCode: generateSubtaskCode(7),
    subtaskName: 'Fixture Procurement Requirements Analysis',
    milestoneName: 'Fixture Procurement',
    assignee: 'user006',
    status: 'Finished',
    gmtCreate: '2024-01-08T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-09T09:00:00Z',
    startDate: '2024-01-09T10:15:00Z',
    dueDate: '2024-01-12T18:00:00Z',
    completionDate: '2024-01-13T17:45:00Z',
    executionNotes: 'Completed fixture procurement requirements analysis and generated purchase orders. All vendor negotiations have been finalized and orders have been placed.',
    deliverables: 'Purchase orders, Vendor contracts',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(7), 'Finished'),
    autoStatusUpdate: true,
    overdueFlag: true, // This one was completed after due date
    planEndDate: '2024-02-15T18:00:00Z',
    finalStatusDate: '2024-01-13T17:45:00Z',
    planCode: 'TEMP004_08_20250225',
    planName: 'National Emergency Recall Plan_W08'
  },
  {
    subtaskCode: generateSubtaskCode(8),
    subtaskName: 'Final Display Diagram Review',
    milestoneName: 'Algorithm Generated Planogram for Review',
    assignee: 'user007',
    status: 'Active',
    gmtCreate: '2024-01-15T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-16T09:00:00Z',
    dueDate: '2024-01-21T18:00:00Z',
    deliverables: 'Final display diagram, Review confirmation',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(8), 'Active'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP002_05_20250210',
    planName: 'Southwest Regular Maintenance Plan_W05'
  },
  {
    subtaskCode: generateSubtaskCode(9),
    subtaskName: 'Store Execution Task Distribution',
    milestoneName: 'Store Task Generation',
    assignee: 'user008',
    status: 'Pending',
    gmtCreate: '2024-01-15T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-25T09:00:00Z',
    dueDate: '2024-01-26T18:00:00Z',
    deliverables: 'Store execution tasks, Distribution records',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(9), 'Pending'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP003_07_20250220',
    planName: 'Northeast Minor Optimization Plan_W07'
  },
  {
    subtaskCode: generateSubtaskCode(10),
    subtaskName: 'Legacy Product Category Cleanup',
    milestoneName: 'Display Group Adjustment',
    assignee: 'user001',
    status: 'Cancelled',
    gmtCreate: '2024-01-05T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-06T09:00:00Z',
    dueDate: '2024-01-10T18:00:00Z',
    deliverables: 'Category cleanup report, Updated product mappings',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(10), 'Cancelled'),
    autoStatusUpdate: false,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP001_52_20250127',
    planName: 'East China Major Adjustment Plan_W52'
  },
  {
    subtaskCode: generateSubtaskCode(11),
    subtaskName: 'Store Layout Design Optimization',
    milestoneName: 'Store Layout Adjustment',
    assignee: 'user004',
    status: 'Ongoing',
    gmtCreate: '2024-01-08T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-09T09:00:00Z',
    startDate: '2024-01-09T10:30:00Z',
    dueDate: '2024-01-12T18:00:00Z',
    executionNotes: 'Optimizing store layout design based on customer flow analysis and product placement strategies. Completed initial draft and currently reviewing with department head.',
    deliverables: 'Optimized layout design, Customer flow analysis',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(11), 'Ongoing'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP004_04_20250201',
    planName: 'North China Emergency Adjustment Plan_W04'
  },
  {
    subtaskCode: generateSubtaskCode(12),
    subtaskName: 'Display Group Performance Analysis',
    milestoneName: 'Display Group Adjustment',
    assignee: 'user009',
    status: 'Active',
    gmtCreate: '2024-01-12T09:00:00Z',
    creatorName: 'system',
    milestoneStartDate: '2024-01-13T09:00:00Z',
    dueDate: '2024-01-16T18:00:00Z',
    deliverables: 'Performance analysis report, Recommendations',
    statusChangeHistory: createStatusHistory(generateSubtaskCode(12), 'Active'),
    autoStatusUpdate: true,
    overdueFlag: false,
    planEndDate: '2024-02-15T18:00:00Z',
    planCode: 'TEMP001_52_20250127',
    planName: 'East China Major Adjustment Plan_W52'
  }
];

// Mock comments data
export const MOCK_COMMENTS: Record<string, SubtaskComment[]> = {
  [generateSubtaskCode(1)]: [
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(1),
      content: 'Started working on display group configuration. Identified 3 potential optimization points in current layout.',
      author: 'user001',
      gmtCreate: '2024-01-16T10:30:00Z'
    },
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(1),
      content: 'Please review the initial configuration proposal. I\'ve attached the draft diagram for your feedback.',
      author: 'Manager Zhang',
      gmtCreate: '2024-01-16T11:15:00Z'
    },
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(1),
      content: 'Configuration proposal looks good. Please proceed with detailed implementation after confirming with Supervisor Li.',
      author: 'Director Liu',
      gmtCreate: '2024-01-16T14:20:00Z'
    }
  ],
  [generateSubtaskCode(3)]: [
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(3),
      content: 'Benchmark drawing creation is 80% complete. Working on finalizing technical specifications.',
      author: 'user003',
      gmtCreate: '2024-01-12T16:45:00Z'
    },
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(3),
      content: 'Excellent, please ensure all technical specifications are documented properly.',
      author: 'Supervisor Wang',
      gmtCreate: '2024-01-13T09:20:00Z'
    },
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(3),
      content: 'All benchmark drawings have completed review and meet technical requirements.',
      author: 'user003',
      gmtCreate: '2024-01-14T15:30:00Z'
    }
  ],
  [generateSubtaskCode(10)]: [
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(10),
      content: 'Started working on legacy product category analysis. Found several outdated mappings that need cleanup.',
      author: 'user001',
      gmtCreate: '2024-01-06T10:30:00Z'
    },
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(10),
      content: 'Due to recent planogram strategy changes, this task is no longer required. Cancelling the task.',
      author: 'Manager Zhang',
      gmtCreate: '2024-01-07T14:20:00Z'
    }
  ],
  [generateSubtaskCode(11)]: [
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(11),
      content: 'Initial layout concepts have been drafted. Working on alternative designs for different store sizes.',
      author: 'user004',
      gmtCreate: '2024-01-10T11:15:00Z'
    },
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(11),
      content: 'Layout concepts have been reviewed and approved. Proceeding to detailed implementation phase.',
      author: 'Supervisor Chen',
      gmtCreate: '2024-01-11T16:45:00Z'
    }
  ]
};

// Mock attachments data
export const MOCK_ATTACHMENTS: Record<string, SubtaskAttachment[]> = {
  [generateSubtaskCode(1)]: [
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(1),
      fileName: 'Display_Group_Configuration_Draft.pdf',
      fileSize: 2048576, // 2MB
      fileType: 'application/pdf',
      uploadTime: '2024-01-16T15:30:00Z',
      uploader: 'user001',
      downloadUrl: '/api/files/download/configuration-draft.pdf'
    },
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(1),
      fileName: 'Sub_Class_Association_Mapping.xlsx',
      fileSize: 1536000, // 1.5MB
      fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      uploadTime: '2024-01-16T16:45:00Z',
      uploader: 'user001',
      downloadUrl: '/api/files/download/association-mapping.xlsx'
    }
  ],
  [generateSubtaskCode(3)]: [
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(3),
      fileName: 'Benchmark_Display_Diagram.pdf',
      fileSize: 512000, // 500KB
      fileType: 'application/pdf',
      uploadTime: '2024-01-14T16:00:00Z',
      uploader: 'user003',
      downloadUrl: '/api/files/download/benchmark-diagram.pdf'
    },
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(3),
      fileName: 'Technical_Specifications.docx',
      fileSize: 256000, // 250KB
      fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      uploadTime: '2024-01-14T16:25:00Z',
      uploader: 'user003',
      downloadUrl: '/api/files/download/technical-specs.docx'
    }
  ],
  [generateSubtaskCode(7)]: [
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(7),
      fileName: 'Purchase_Orders_Summary.xlsx',
      fileSize: 3072000, // 3MB
      fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      uploadTime: '2024-01-13T17:30:00Z',
      uploader: 'user006',
      downloadUrl: '/api/files/download/purchase-orders.xlsx'
    },
    {
      id: generateId(),
      subtaskCode: generateSubtaskCode(7),
      fileName: 'Vendor_Contracts.pdf',
      fileSize: 1024000, // 1MB
      fileType: 'application/pdf',
      uploadTime: '2024-01-13T17:40:00Z',
      uploader: 'user006',
      downloadUrl: '/api/files/download/vendor-contracts.pdf'
    }
  ]
};

// Mock reference data - aligned with planogram plan template milestones
export const MOCK_MILESTONES = [
  'Display Group Adjustment',
  'Store Group Setup',
  'Benchmark Master Drawing',
  'Store Layout Adjustment',
  'Fixture Type Set Up',
  'Display Planning Adjustment',
  'Fixture Procurement',
  'Algorithm Generated Planogram for Review',
  'Store Task Generation'
];

export const MOCK_ASSIGNEES = [
  'user001',
  'user002',
  'user003',
  'user004',
  'user005',
  'user006',
  'user007',
  'user008'
];

// Helper functions
export const getSubtaskByCode = (code: string): PlanogramSubtask | undefined => {
  return MOCK_SUBTASKS.find(subtask => subtask.subtaskCode === code);
};

export const getCommentsBySubtaskCode = (subtaskCode: string): SubtaskComment[] => {
  return MOCK_COMMENTS[subtaskCode] || [];
};

export const getAttachmentsBySubtaskCode = (subtaskCode: string): SubtaskAttachment[] => {
  return MOCK_ATTACHMENTS[subtaskCode] || [];
};

export const addCommentToSubtask = (subtaskCode: string, comment: SubtaskComment): void => {
  if (!MOCK_COMMENTS[subtaskCode]) {
    MOCK_COMMENTS[subtaskCode] = [];
  }
  MOCK_COMMENTS[subtaskCode].push(comment);
};

export const addAttachmentToSubtask = (subtaskCode: string, attachment: SubtaskAttachment): void => {
  if (!MOCK_ATTACHMENTS[subtaskCode]) {
    MOCK_ATTACHMENTS[subtaskCode] = [];
  }
  MOCK_ATTACHMENTS[subtaskCode].push(attachment);
};

export const removeAttachmentFromSubtask = (attachmentId: string): boolean => {
  for (const subtaskCode in MOCK_ATTACHMENTS) {
    const attachments = MOCK_ATTACHMENTS[subtaskCode];
    const index = attachments.findIndex(att => att.id === attachmentId);
    if (index !== -1) {
      attachments.splice(index, 1);
      return true;
    }
  }
  return false;
};